import { css } from '@emotion/react';
import { Box, Text, Title, desktopFirstMediaQuery } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LANDING_COLOR, LANDING_FONT, LANDING_SPACE } from '../landing/constants';
import {
  DEFAULT_HERO_FRONT_INDEX,
  INTERVIEWERS,
  type UserState,
  getCardBottomColor,
  getCardImageFadeGradient,
} from './interviewers';

const NARROW_BREAKPOINT_PX = 900;
const MANUAL_PAUSE_MS = 6000;
const DEFAULT_AUTOPLAY_SECONDS = 3.4;
/** 첫 진입 시 카드가 모였다가 펼쳐지는 연출 */
const ENTRANCE_SPREAD_MS = 900;
const ENTRANCE_START_DELAY_MS = 120;
/** 카드 크기 — 기존 히어로 스케일 유지 */
const CARD_WIDTH_DESKTOP = 300;
const CARD_HEIGHT_DESKTOP = 440;
const CARD_WIDTH_NARROW = 196;
const CARD_HEIGHT_NARROW = 288;
/** CDN 전신 PNG — cover 프레이밍 (레퍼런스 피커: 카드 폭 ~86%, 상반신 중심) */
const HERO_IMAGE_FRAME: Record<string, { height: string; width: string; focus: string }> = {
  dora: { height: '88%', width: '90%', focus: 'center 30%' },
  taeri: { height: '92%', width: '94%', focus: 'center 24%' },
  hyeongseok: { height: '94%', width: '96%', focus: 'center 22%' },
  hyangro: { height: '94%', width: '96%', focus: 'center 24%' },
  seonggyeom: { height: '92%', width: '94%', focus: 'center 26%' },
  hyejeong: { height: '90%', width: '92%', focus: 'center 26%' },
};
const DEFAULT_HERO_IMAGE_FRAME = { height: '92%', width: '94%', focus: 'center 25%' };
/** 사진·텍스트 경계 캡션 높이 */
const CARD_CAPTION_RATIO = 0.28;

export type StageLayout = 'tight' | 'wide';

export type HeroStageProps = {
  /** 방문자 상태. 서버 세션/이용권 결과를 그대로 넘기세요. */
  userState?: UserState;
  /** 이탈 지점 단계 (IN_PROGRESS 넛지에 표시) */
  resumeStep?: number;
  /** tight = 겹치며 안쪽을 향함(권장) / wide = 넓게 펼침 */
  stageLayout?: StageLayout;
  /** 자동 회전 on/off */
  autoplay?: boolean;
  /** 자동 회전 간격(초) */
  autoplaySeconds?: number;
};

/**
 * 커리어 스파링 — 히어로 (3D 링 캐러셀 면접관 스테이지)
 *
 * - 카드가 원(ring) 위에 놓여 회전. tight 는 안쪽(중앙)을 향해 둘러싸는 느낌
 * - 중앙 카드만 크게(1.16x) + 대사가 이름 아래로 펼쳐짐
 * - autoplay 자동 회전 / hover 정지 / 직접 조작 시 6초 후 재개
 */
export const HeroStage = ({
  userState = 'NEW',
  resumeStep = 3,
  stageLayout = 'tight',
  autoplay = true,
  autoplaySeconds = DEFAULT_AUTOPLAY_SECONDS,
}: HeroStageProps) => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(DEFAULT_HERO_FRONT_INDEX);
  const [paused, setPaused] = useState(false);
  const [narrow, setNarrow] = useState(false);
  /** gather → spread → ready : 첫 진입 펼침 연출 단계 */
  const [entrancePhase, setEntrancePhase] = useState<'gather' | 'spread' | 'ready'>('gather');
  /** sticky 히어로 구간 스크롤 진행(0~1) — 카드 머지 + 카피 등장에 사용 */
  const [scrollProgress, setScrollProgress] = useState(0);
  const stageWrapRef = useRef<HTMLDivElement | null>(null);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const n = INTERVIEWERS.length;
  const tight = stageLayout === 'tight';
  const isSpread = entrancePhase !== 'gather';
  /** 카드 머지: 스크롤 중반부터 — 구간을 넓혀 서서히 모임 */
  const merge = smoothstep(0.16, 0.64, scrollProgress);
  /** 히어로 타이틀 퇴장 — 구간을 넓혀 급격한 끊김 완화 */
  const headlineOut = smootherstep(smoothstep(0.1, 0.58, scrollProgress));
  /** 카드 스테이지 퇴장 — 타이틀보다 살짝 늦게, 같은 Z-depth 연출 */
  const stageOut = smootherstep(smoothstep(0.16, 0.64, scrollProgress));
  /** 스크롤 카피 등장 — 퇴장과 살짝 겹치되 급격한 교차 fade 방지 */
  const introIn = smootherstep(smoothstep(0.36, 0.72, scrollProgress));

  useEffect(function trackNarrowViewport() {
    const onResize = () => {
      setNarrow(window.innerWidth < NARROW_BREAKPOINT_PX);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return function stopTrackingNarrowViewport() {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(function spreadCardsOnEntrance() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setEntrancePhase('ready');
      return;
    }

    let spreadTimer: ReturnType<typeof setTimeout> | null = null;
    let readyTimer: ReturnType<typeof setTimeout> | null = null;
    const raf = requestAnimationFrame(() => {
      spreadTimer = setTimeout(() => {
        setEntrancePhase('spread');
        readyTimer = setTimeout(() => {
          setEntrancePhase('ready');
        }, ENTRANCE_SPREAD_MS + 120);
      }, ENTRANCE_START_DELAY_MS);
    });

    return function cancelEntranceSpread() {
      cancelAnimationFrame(raf);
      if (spreadTimer) {
        clearTimeout(spreadTimer);
      }
      if (readyTimer) {
        clearTimeout(readyTimer);
      }
    };
  }, []);

  useEffect(
    function rotateCarouselWhileAutoplay() {
      if (!autoplay || entrancePhase !== 'ready' || merge > 0.12) {
        return;
      }

      const ms = Math.max(1200, autoplaySeconds * 1000);
      const id = setInterval(() => {
        if (paused) {
          return;
        }
        setIdx((i) => (i + 1) % n);
      }, ms);

      return function stopCarouselRotation() {
        clearInterval(id);
      };
    },
    [autoplay, autoplaySeconds, paused, n, entrancePhase, merge],
  );

  useEffect(function clearResumeTimerOnUnmount() {
    return function clearResumeTimer() {
      if (resumeTimer.current) {
        clearTimeout(resumeTimer.current);
      }
    };
  }, []);

  useEffect(function trackHeroScrollProgress() {
    let raf = 0;
    const update = () => {
      raf = 0;
      const wrap = stageWrapRef.current;
      if (!wrap) {
        return;
      }
      const rect = wrap.getBoundingClientRect();
      const travel = Math.max(1, wrap.offsetHeight - window.innerHeight);
      const next = Math.min(1, Math.max(0, -rect.top / travel));
      setScrollProgress((prev) => (Math.abs(prev - next) < 0.003 ? prev : next));
    };
    const onScroll = () => {
      if (raf) {
        return;
      }
      raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return function stopHeroScrollProgress() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, []);

  const nudge = useCallback(
    (next: number) => {
      setIdx(((next % n) + n) % n);
      setPaused(true);
      if (resumeTimer.current) {
        clearTimeout(resumeTimer.current);
      }
      resumeTimer.current = setTimeout(() => {
        setPaused(false);
      }, MANUAL_PAUSE_MS);
    },
    [n],
  );

  /** 오프셋은 최단경로 순환 — 중앙에서 2칸 넘게 벗어난 카드는 숨김 */
  const offset = (i: number) => {
    const half = Math.floor(n / 2);
    return ((i - idx + n + half) % n) - half;
  };

  const cardBackground = (i: number): string => INTERVIEWERS[i].cardBg;

  const cardStyle = (i: number): CSSProperties => {
    const d = offset(i);
    const a = Math.abs(d);
    const centered = d === 0;
    const hidden = a > 2;

    // 링 간격(spacingDeg)과 얼굴 기울기(tiltDeg)를 분리
    const spacingDeg = narrow ? (tight ? 26 : 20) : tight ? 24 : 17;
    const nearTiltDeg = narrow ? (tight ? 36 : 30) : tight ? 34 : 26;
    const farTiltDeg = narrow ? (tight ? 40 : 34) : tight ? 38 : 30;
    const tiltAmount = a === 0 ? 0 : a === 1 ? nearTiltDeg : farTiltDeg;
    const theta = (spacingDeg * Math.PI) / 180;
    const R = narrow ? (tight ? 360 : 520) : tight ? 540 : 820;
    const sign = tight ? -1 : 1;

    // 진입 전: 중앙에 모여 있다가 isSpread 시 링 위치로 펼침
    // 커버 스크롤(merge): 중앙으로 모이며 블러·페이드로 자연스럽게 사라짐
    const mergeEase = smootherstep(merge);
    const spreadT = isSpread ? 1 - mergeEase : 0;

    const x = spreadT * R * Math.sin(d * theta);
    const y = spreadT * a * (narrow ? 8 : 14);
    const z = spreadT * (R * Math.cos(d * theta) - R) + (1 - spreadT) * -a * 8;
    const rotateY = spreadT * Math.sign(d || 1) * tiltAmount * sign;
    const scale = !isSpread
      ? centered
        ? 1.08
        : 1
      : (centered ? 1.16 : (tight ? 0.95 : 0.98) - a * (tight ? 0.055 : 0.11)) *
          (1 - mergeEase * 0.12) +
        mergeEase * 0.92;
    const brightness =
      (!isSpread || centered ? 1 : 1 - a * 0.16) * (1 - mergeEase * 0.28) + mergeEase * 0.62;
    const blurPx = mergeEase * (centered ? 10 : 14 + a * 4);
    const durationMs =
      merge > 0.01
        ? 0
        : entrancePhase === 'ready'
          ? 620
          : entrancePhase === 'spread'
            ? ENTRANCE_SPREAD_MS
            : 0;
    const staggerMs = entrancePhase === 'spread' && merge < 0.02 ? a * 55 : 0;

    return {
      position: 'absolute',
      left: 0,
      top: narrow ? 48 : 72,
      width: narrow ? CARD_WIDTH_NARROW : CARD_WIDTH_DESKTOP,
      height: narrow ? CARD_HEIGHT_NARROW : CARD_HEIGHT_DESKTOP,
      background: cardBackground(i),
      transformOrigin: '50% 50%',
      transform: [
        'translate(-50%,0)',
        `translate3d(${x}px, ${y}px, ${z}px)`,
        `rotateY(${rotateY}deg)`,
        `scale(${scale})`,
      ].join(' '),
      filter: `brightness(${brightness}) blur(${blurPx}px)`,
      opacity: hidden ? 0 : Math.max(0, 1 - mergeEase * 0.88),
      visibility: hidden ? 'hidden' : 'visible',
      pointerEvents: hidden || !isSpread || merge > 0.2 ? 'none' : 'auto',
      zIndex: 20 - a,
      boxShadow: centered
        ? `0 ${44 - mergeEase * 28}px ${80 - mergeEase * 40}px -26px rgba(0,0,0,${0.55 - mergeEase * 0.35})`
        : `0 28px 50px -22px rgba(0,0,0,${0.45 - mergeEase * 0.3})`,
      transition: [
        `transform ${durationMs}ms cubic-bezier(.22,.61,.36,1) ${staggerMs}ms`,
        `filter ${durationMs}ms ease ${staggerMs}ms`,
        `opacity ${merge > 0.01 ? 0 : 180}ms linear`,
        `box-shadow ${durationMs}ms ease ${staggerMs}ms`,
      ].join(', '),
      willChange: merge > 0.01 ? 'transform, opacity' : undefined,
    };
  };

  /** 대사는 이름 아래. 정면 카드에서만 펼쳐진다 */
  const quoteStyle = (i: number): CSSProperties => {
    const centered = i === idx && entrancePhase === 'ready' && merge < 0.2;
    return {
      margin: '7px 0 0',
      fontSize: 13.5,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '-0.01em',
      color: LANDING_COLOR.bodyMuted,
      textWrap: 'pretty',
      maxHeight: centered ? 48 : 0,
      opacity: centered ? 1 : 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      transition: 'opacity 320ms ease 100ms, max-height 380ms cubic-bezier(.2,0,.2,1)',
    };
  };

  const mergeEase = smootherstep(merge);
  const faceFade = Math.max(0, 1 - mergeEase * 1.15);
  const headlineStyle: CSSProperties = {
    transform: [
      `translate3d(0, ${headlineOut * -18}px, ${headlineOut * -100}px)`,
      `scale(${1 - headlineOut * 0.1})`,
    ].join(' '),
    opacity: Math.max(0, 1 - headlineOut),
    filter: headlineOut > 0.04 ? `blur(${headlineOut * 3.5}px)` : 'none',
    transformOrigin: '50% 40%',
    willChange: 'transform, opacity, filter',
    transition: merge > 0.02 ? 'none' : 'transform 280ms ease, opacity 280ms ease',
    pointerEvents: headlineOut > 0.55 ? 'none' : 'auto',
  };

  const introStyle: CSSProperties = {
    opacity: introIn,
    transform: `translate3d(0, ${(1 - introIn) * 20}px, 0) scale(${0.97 + introIn * 0.03})`,
    pointerEvents: introIn > 0.45 ? 'auto' : 'none',
    willChange: 'transform, opacity',
  };

  const stageOuterStyle: CSSProperties = {
    transform: [
      `translate3d(0, ${stageOut * -16}px, ${stageOut * -90}px)`,
      `scale(${1 - stageOut * 0.1})`,
    ].join(' '),
    opacity: Math.max(0, 1 - Math.max(stageOut, introIn * 0.75)),
    filter: stageOut > 0.04 ? `blur(${stageOut * 3}px)` : 'none',
    transformOrigin: '50% 45%',
    willChange: 'transform, opacity, filter',
    transition:
      merge > 0.02 ? 'none' : 'transform 280ms ease, opacity 280ms ease, filter 280ms ease',
    pointerEvents: stageOut > 0.6 || introIn > 0.5 ? 'none' : 'auto',
    visibility: stageOut > 0.98 || introIn > 0.97 ? 'hidden' : 'visible',
  };

  return (
    <Box ref={stageWrapRef} css={styles.stageWrap} data-hero-stage>
      <Box component="section" css={styles.section}>
        <Box css={styles.content}>
          <Box css={styles.headline} style={headlineStyle}>
            <Title order={1} css={styles.title}>
              {t('ai_interviews.hero.headline_v2', '나를 위한 진짜 면접 연습')}
            </Title>
            <Text css={styles.subtitle}>
              {t(
                'ai_interviews.hero.desc1',
                '성향이 다른 AI 면접관들과 진짜처럼 대화하는 실전 모의면접.',
              )}
              <br />
              {t('ai_interviews.hero.desc2', '이력서만 올리면 지금 바로 시작돼요.')}
            </Text>
          </Box>

          <Box css={styles.stageOuter} style={stageOuterStyle}>
            <Box
              css={styles.stage}
              style={{ perspective: 1900, perspectiveOrigin: '50% 44%' }}
              onMouseEnter={() => {
                setPaused(true);
              }}
              onMouseLeave={() => {
                setPaused(false);
              }}
            >
              <Box css={styles.stageAxis} style={{ transformStyle: 'preserve-3d' }}>
                {INTERVIEWERS.map((person, i) => {
                  const imageFrame = HERO_IMAGE_FRAME[person.key] ?? DEFAULT_HERO_IMAGE_FRAME;
                  return (
                    <button
                      key={person.key}
                      type="button"
                      aria-label={t('ai_interviews.hero.view_aria', {
                        name: t(person.nameKey, ''),
                        defaultValue: '',
                      })}
                      aria-current={i === idx}
                      onClick={() => {
                        nudge(i);
                      }}
                      css={styles.card}
                      style={cardStyle(i)}
                    >
                      <Box aria-hidden css={styles.cardPhoto}>
                        {/* eslint-disable-next-line @next/next/no-img-element -- 어드민 CDN 전신 캐릭터 */}
                        <img
                          src={person.imageSrc}
                          alt={t('ai_interviews.hero.interviewer_alt', {
                            name: t(person.nameKey, ''),
                            defaultValue: '',
                          })}
                          width={2752}
                          height={1536}
                          css={styles.cardImage}
                          style={{
                            height: imageFrame.height,
                            width: imageFrame.width,
                            objectPosition: imageFrame.focus,
                            opacity: faceFade,
                            transition: 'opacity 160ms linear',
                          }}
                        />
                        <Box
                          css={styles.cardImageFade}
                          style={{ background: getCardImageFadeGradient(person.cardBg) }}
                        />
                      </Box>
                      <Box
                        css={styles.cardCaption}
                        style={{
                          background: getCardBottomColor(person.cardBg),
                          opacity: faceFade,
                          transition: 'opacity 160ms linear',
                        }}
                      >
                        <Text
                          css={[
                            styles.cardTag,
                            person.special ? styles.cardTagSpecial : styles.cardTagDefault,
                          ]}
                        >
                          {person.special
                            ? t('ai_interviews.hero.special', '스페셜')
                            : t('ai_interviews.hero.tag_with_difficulty', {
                                tag: t(person.tagKey, ''),
                                stars: '★'.repeat(person.difficulty),
                                defaultValue: '',
                              })}
                        </Text>
                        <Text css={styles.cardName}>{t(person.nameKey, '')}</Text>
                        <Text css={styles.cardQuote} style={quoteStyle(i)}>
                          {t(person.quoteKey, '')}
                        </Text>
                      </Box>
                    </button>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {userState === 'IN_PROGRESS' ? (
            <Box css={styles.nudge}>
              {t('ai_interviews.hero.resume_nudge', { step: resumeStep })}
            </Box>
          ) : null}
        </Box>

        <Box css={styles.introOverlay} style={introStyle} aria-hidden={introIn < 0.05}>
          <Title order={2} css={styles.introTitle}>
            {t('ai_interviews.landing.hero.intro_title', '진짜 면접처럼 파고들어요')}
          </Title>
          <Text css={styles.introSub}>
            {t('ai_interviews.landing.hero.intro_body1', '남들과 똑같은 질문은 없어요.')}
            <br />
            {t(
              'ai_interviews.landing.hero.intro_body2',
              '내 이력서를 읽은 면접관이 나에게 맞는 질문을 던져요.',
            )}
            <br />
            {t(
              'ai_interviews.landing.hero.intro_body3',
              '내 답변을 듣고 이어지는 꼬리질문으로, 실제 면접의 흐름을 그대로 재현해요.',
            )}
            <br />
            <br />
            {t('ai_interviews.landing.hero.intro_body4', '외우는 면접은 이제 그만.')}
            <br />
            {t(
              'ai_interviews.landing.hero.intro_body5',
              '진짜 실력으로 어디까지 갈 수 있는지 보여주세요.',
            )}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  /** sticky 히어로 스크롤 런웨이 — 원티드 대비 짧게 유지 */
  stageWrap: css({
    position: 'relative',
    height: '140vh',
  }),
  section: css({
    position: 'sticky',
    top: 0,
    zIndex: 0,
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    background: 'radial-gradient(120% 100% at 50% 0%, #2C3239 0%, #16191D 58%, #0E1013 100%)',
    padding: `${LANDING_SPACE.heroTop} ${LANDING_SPACE.heroX} 0`,
  }),
  content: css({
    position: 'relative',
    zIndex: 1,
    perspective: 1400,
    perspectiveOrigin: '50% 30%',
  }),
  introOverlay: css({
    position: 'absolute',
    left: 0,
    right: 0,
    top: '45%',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: `0 ${LANDING_SPACE.heroX}`,
    textAlign: 'center',
    pointerEvents: 'none',
  }),
  introTitle: css({
    margin: 0,
    color: '#fff',
    fontSize: LANDING_FONT.display,
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.04em',
    textWrap: 'balance',
    wordBreak: 'keep-all',
  }),
  introSub: css({
    marginTop: LANDING_SPACE.introStack,
    maxWidth: 1200,
    fontSize: LANDING_FONT.introBody,
    lineHeight: 1.7,
    wordBreak: 'keep-all',
    color: 'rgba(255,255,255,0.88)',
  }),
  headline: css({
    transformStyle: 'preserve-3d',
    backfaceVisibility: 'hidden',
  }),
  title: css({
    margin: 0,
    textAlign: 'center',
    color: '#fff',
    fontSize: LANDING_FONT.display,
    fontWeight: 800,
    lineHeight: 1.14,
    letterSpacing: '-0.042em',
    textWrap: 'balance',
  }),
  subtitle: css({
    marginTop: LANDING_SPACE.leadStack,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.6)',
    fontSize: LANDING_FONT.lead,
    lineHeight: 1.6,
    textWrap: 'pretty',
  }),
  stageOuter: css({
    marginTop: LANDING_SPACE.introStack,
    marginBottom: 0,
    overflow: 'hidden',
    background: 'transparent',
    transition: 'opacity 180ms linear, max-height 420ms ease, margin 420ms ease',
  }),
  stage: css({
    position: 'relative',
    // 카드 top(72) + height(440) + scale 여유
    height: 552,
    width: '100%',
    maxWidth: 1100,
    margin: '0 auto',
    overflow: 'visible',
    background: 'transparent',
    [desktopFirstMediaQuery.mobile]: {
      height: 356,
    },
  }),
  stageAxis: css({
    position: 'absolute',
    left: '50%',
    top: 0,
    height: '100%',
    width: 0,
    background: 'transparent',
  }),
  card: css({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 22,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    appearance: 'none',
    backgroundColor: 'transparent',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
  }),
  /** 캡션 위 사진 영역 — CDN 전신 PNG 프레이밍 */
  cardPhoto: css({
    position: 'absolute',
    insetInline: 0,
    top: 0,
    bottom: `${CARD_CAPTION_RATIO * 100}%`,
    overflow: 'hidden',
    pointerEvents: 'none',
  }),
  cardImage: css({
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translateX(-50%)',
    objectFit: 'cover',
    pointerEvents: 'none',
  }),
  /** 인물 하단 → 카드 배경색 페이드 (사진·텍스트 경계) */
  cardImageFade: css({
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    height: '36%',
    pointerEvents: 'none',
  }),
  cardCaption: css({
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    minHeight: `${CARD_CAPTION_RATIO * 100}%`,
    padding: `${LANDING_SPACE.cardCaptionPad} ${LANDING_SPACE.cardCaptionPad} 20px`,
    textAlign: 'left',
    color: LANDING_COLOR.ink,
  }),
  cardTag: css({
    fontSize: LANDING_FONT.micro,
    fontWeight: 700,
    lineHeight: 1.4,
  }),
  cardTagDefault: css({
    color: LANDING_COLOR.caption,
  }),
  cardTagSpecial: css({
    color: '#D6336C',
  }),
  cardName: css({
    marginTop: 3,
    fontSize: LANDING_FONT.cardName,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: LANDING_COLOR.ink,
    lineHeight: 1.3,
  }),
  cardQuote: css({
    // maxHeight/opacity는 quoteStyle(inline)에서 제어
  }),
  nudge: css({
    margin: `${LANDING_SPACE.introStack} auto 0`,
    display: 'flex',
    width: 'fit-content',
    alignItems: 'center',
    gap: 10,
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.08)',
    padding: `${LANDING_SPACE.chipPadY} ${LANDING_SPACE.chipPadX}`,
    fontSize: LANDING_FONT.caption,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.85)',
  }),
};

/** 0~1 hermite smoothstep */
const smoothstep = (edge0: number, edge1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/** 더 부드러운 5차 smootherstep — 머지 모션용 */
const smootherstep = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * x * (x * (x * 6 - 15) + 10);
};
